import { useCallback, useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import { getMaxxSemanticStage } from "../../world-model/maxx";
import { getMaxxSemanticActions } from "../../world-model/maxxActions";
import { verifyPendingMaxxAction } from "../../world-model/maxxActionVerification";
import {
  publishLiveAppContext,
  recordLiveCameraTelemetry,
} from "../../world-model/liveAppPerception";
import {
  describeCameraView,
  describeObjectSpatialRelation,
} from "../../world-model/spatialPerception";
import { resolvePortalSpatialView } from "../../world-model/portalSpatialModel";
import { readWorldxCenterEvidence } from "../../world-model/worldxCenterEvidence";

const MAX_SCENE_OBJECTS = 200;
const RENDERER_STATS_INTERVAL_SECONDS = 0.5;
const CONTROLLED_GLB_TARGET_NAME_HINTS = Object.freeze({
  meet_joz_flex: ["vibe", "jk", "ss.001"],
  meet_joz_ascend: ["discover", "gg"],
  meet_joz_mogg: ["mogg", "mogg1", "skills", "jj"],
  meet_joz_skills: ["skills", "mogg", "mogg1", "xx", "xx.010", "xx.07"],
});

function publishScenePerformanceStats(partialStats) {
  if (typeof window === "undefined") return;
  const stats = {
    ...(window.__scenePerformanceStats || {}),
    ...partialStats,
  };
  window.__scenePerformanceStats = stats;
  window.dispatchEvent(new CustomEvent("world-scene-performance", {
    detail: stats,
  }));
}

function vectorValues(vector) {
  return vector ? [vector.x, vector.y, vector.z] : null;
}

function publishCanvasCameraInteraction(
  camera,
  active,
  direction = new THREE.Vector3(),
  { portal = "root", stage = null, mesh = null } = {},
  previousDirection = null,
) {
  if (typeof window === "undefined" || !camera) return;
  direction.set(0, 0, -1).applyQuaternion(camera.quaternion);
  const view = describeCameraView(vectorValues(direction), previousDirection);
  const lookAtEvidence = resolvePortalSpatialView({
    portal,
    stage,
    mesh,
    yawDegrees: view.yaw_degrees,
    pitchDegrees: view.pitch_degrees,
  });
  recordLiveCameraTelemetry({
    portal,
    cameraView: view,
    lookAtEvidence,
    active,
  });
  window.dispatchEvent(new CustomEvent("world-camera-interaction", {
    detail: {
      active: active === true,
      cameraState: { view },
      lookAtEvidence,
    },
  }));
}

function signedAngleDeltaDegrees(current, previous) {
  const delta = Number(current) - Number(previous);
  if (!Number.isFinite(delta)) return 0;
  return ((delta + 540) % 360) - 180;
}

function cameraDynamics(view, previousSample, elapsed, portal) {
  const matchesPortal = previousSample?.portal === portal;
  const deltaSeconds = matchesPortal ? elapsed - previousSample.elapsed : 0;
  const validInterval = deltaSeconds > 0 && deltaSeconds < 0.5;
  const yawVelocity = validInterval
    ? signedAngleDeltaDegrees(view.yaw_degrees, previousSample.yawDegrees) / deltaSeconds
    : 0;
  const pitchVelocity = validInterval
    ? (view.pitch_degrees - previousSample.pitchDegrees) / deltaSeconds
    : 0;
  const angularSpeed = Math.hypot(yawVelocity, pitchVelocity);
  return {
    yaw_velocity: Number(yawVelocity.toFixed(1)),
    pitch_velocity: Number(pitchVelocity.toFixed(1)),
    stability: Math.round(THREE.MathUtils.clamp(100 - angularSpeed / 2.5, 0, 100)),
  };
}

function objectId(object) {
  return String(
    object?.userData?.entityId ||
    object?.userData?.objectId ||
    object?.userData?.meshId ||
    object?.name ||
    ""
  ).trim().toLowerCase();
}

function normalizeTrackedObjectId(value) {
  return String(value || "").trim().toLowerCase();
}

function objectNameMatchesAny(object, names = []) {
  let current = object;
  while (current) {
    const objectName = String(current.name || "").trim().toLowerCase();
    if (
      objectName &&
      names.some((name) =>
        objectName === name ||
        objectName.startsWith(`${name}.`) ||
        objectName.startsWith(`${name}_`) ||
        objectName.startsWith(`${name}-`) ||
        objectName.includes(name)
      )
    ) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

function walkSceneGraph(root, visitor) {
  if (!root || typeof visitor !== "function") return;
  const pending = [root];
  const visited = new Set();
  while (pending.length) {
    const object = pending.pop();
    if (!object || visited.has(object)) continue;
    visited.add(object);
    visitor(object);
    const children = Array.isArray(object.children) ? object.children : [];
    for (let index = children.length - 1; index >= 0; index -= 1) {
      pending.push(children[index]);
    }
  }
}

function semanticContext(object) {
  let current = object;
  while (current) {
    const data = current.userData || {};
    if (data.entityId || data.semanticMeaning) {
      return {
        id: String(data.entityId || data.objectId || "").trim().toLowerCase(),
        portalId: data.portalId || null,
        role: data.semanticRole || null,
        meaning: data.semanticMeaning || null,
        children: Array.isArray(data.semanticChildren) ? data.semanticChildren : [],
        interactive: typeof data.interactive === "boolean" ? data.interactive : null,
        action: data.action || null,
        target: data.target || null,
      };
    }
    current = current.parent;
  }
  return null;
}

function isEffectivelyVisible(object) {
  let current = object;
  while (current) {
    if (current.visible === false) return false;
    current = current.parent;
  }
  return true;
}

function materialHasVisibleOutput(material) {
  if (!material || material.visible === false) return false;
  const shaderOpacity = Number(
    material.uOpacity ?? material.uniforms?.uOpacity?.value
  );
  if (Number.isFinite(shaderOpacity) && shaderOpacity <= 0.01) return false;
  const opacity = Number(material.opacity);
  if (Number.isFinite(opacity) && opacity <= 0.01) return false;
  return true;
}

function isVisibleRenderable(object) {
  if (!(object.isMesh || object.isPoints || object.isLine || object.isSprite)) return false;
  const materials = Array.isArray(object.material)
    ? object.material
    : object.material
      ? [object.material]
      : [];
  return materials.length === 0 || materials.some(materialHasVisibleOutput);
}

function matchesTrackedObject(object, trackedObjectId) {
  const targetId = normalizeTrackedObjectId(trackedObjectId);
  if (!targetId) return false;
  let current = object;
  while (current) {
    if (objectId(current) === targetId) return true;
    current = current.parent;
  }
  const semantic = semanticContext(object);
  if (normalizeTrackedObjectId(semantic?.id) === targetId) return true;
  const controlledGlbNameHints = CONTROLLED_GLB_TARGET_NAME_HINTS[targetId];
  if (controlledGlbNameHints) {
    return objectNameMatchesAny(object, controlledGlbNameHints);
  }
  return semantic?.children?.length === 1
    && semantic.children.some((child) => normalizeTrackedObjectId(child?.id) === targetId);
}

function measureVisibleSceneStats(scene, camera, gl, scratch, trackedObjectId = null) {
  const width = gl?.domElement?.width || 0;
  const height = gl?.domElement?.height || 0;
  const normalizedTrackedObjectId = normalizeTrackedObjectId(trackedObjectId);
  if (!scene || !camera) {
    return {
      pixels: 0,
      vertices: 0,
      faces: 0,
      trackedObjectId: normalizedTrackedObjectId || null,
      trackedGeometry: false,
    };
  }

  camera.updateMatrixWorld();
  scratch.projectionScreenMatrix.multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse
  );
  scratch.frustum.setFromProjectionMatrix(scratch.projectionScreenMatrix);

  let visibleVertices = 0;
  let visibleFaces = 0;
  let trackedVertices = 0;
  let trackedFaces = 0;
  let matchedTrackedGeometry = false;
  walkSceneGraph(scene, (object) => {
    if (!isEffectivelyVisible(object) || !(object.isMesh || object.isPoints)) return;
    const geometry = object.geometry;
    if (!geometry?.attributes?.position) return;

    const instanceCount = object.isInstancedMesh ? object.count : 1;
    const objectVertices = geometry.attributes.position.count * instanceCount;
    const objectFaces = object.isMesh
      ? (
          geometry.index
            ? geometry.index.count / 3
            : geometry.attributes.position.count / 3
        ) * instanceCount
      : 0;

    if (matchesTrackedObject(object, normalizedTrackedObjectId)) {
      matchedTrackedGeometry = true;
      trackedVertices += objectVertices;
      trackedFaces += objectFaces;
    }

    if (!geometry.boundingSphere) geometry.computeBoundingSphere();
    if (!geometry.boundingSphere) return;
    scratch.sphere.copy(geometry.boundingSphere).applyMatrix4(object.matrixWorld);
    if (!scratch.frustum.intersectsSphere(scratch.sphere)) return;

    visibleVertices += objectVertices;
    visibleFaces += objectFaces;
  });

  const useTrackedGeometry = Boolean(normalizedTrackedObjectId && matchedTrackedGeometry);
  return {
    pixels: width * height,
    vertices: useTrackedGeometry ? trackedVertices : visibleVertices,
    faces: Math.round(useTrackedGeometry ? trackedFaces : visibleFaces),
    trackedObjectId: normalizedTrackedObjectId || null,
    trackedGeometry: useTrackedGeometry,
  };
}

export function SceneObservationBridge({
  currentPortal,
  currentStage,
  currentMesh,
  isMobile = false,
}) {
  const { scene, camera, gl, controls } = useThree();
  const lastPublishedSignatureRef = useRef("");
  const lastCameraDirectionRef = useRef({ portal: null, direction: null });
  const lastMeaningfulRotationRef = useRef(null);
  const fullCaptureTaskRef = useRef(null);
  const cameraInteractionActiveRef = useRef(false);
  const syncLiveAppContextRef = useRef(false);
  const pointerControlActiveRef = useRef(false);
  const pixelStatsSignatureRef = useRef("");
  const pixelStatsTaskRef = useRef(null);
  const pixelStatsTrackedObjectIdRef = useRef(null);
  const rendererStatsElapsedRef = useRef(0);
  const smoothedFrameMsRef = useRef(16.7);
  const cameraInteractionDirectionRef = useRef(new THREE.Vector3());
  const lastCameraInteractionPublishAtRef = useRef(-Infinity);
  const portalRecoveryRef = useRef(null);
  const pixelStatsScratchRef = useRef({
    frustum: new THREE.Frustum(),
    projectionScreenMatrix: new THREE.Matrix4(),
    sphere: new THREE.Sphere(),
  });

  useEffect(() => {
    const cancelScheduledStats = () => {
      const task = pixelStatsTaskRef.current;
      if (!task) return;
      if (task.type === "idle" && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(task.id);
      } else {
        window.clearTimeout(task.id);
      }
      pixelStatsTaskRef.current = null;
    };
    const measureStats = () => {
      pixelStatsTaskRef.current = null;
      const trackedObjectId = pixelStatsTrackedObjectIdRef.current
        || window.__sceneObservationSnapshot?.lookAtEvidence?.primaryObjectId
        || null;
      const stats = measureVisibleSceneStats(
        scene,
        camera,
        gl,
        pixelStatsScratchRef.current,
        trackedObjectId
      );
      const signature = [
        stats.trackedObjectId || "view",
        stats.trackedGeometry ? "tracked" : "visible",
        stats.pixels,
        stats.vertices,
        stats.faces,
      ].join(":");
      if (signature === pixelStatsSignatureRef.current) return;
      pixelStatsSignatureRef.current = signature;
      publishScenePerformanceStats({ cameraViewStats: stats });
    };
    const scheduleStats = () => {
      cancelScheduledStats();
      if (typeof window.requestIdleCallback === "function") {
        pixelStatsTaskRef.current = {
          type: "idle",
          id: window.requestIdleCallback(measureStats, { timeout: 800 }),
        };
      } else {
        pixelStatsTaskRef.current = {
          type: "timeout",
          id: window.setTimeout(measureStats, 80),
        };
      }
    };
    const handleSceneObserved = (event) => {
      if (event.detail?.runtimeStatus?.interacting) return;
      pixelStatsTrackedObjectIdRef.current = normalizeTrackedObjectId(
        event.detail?.lookAtEvidence?.primaryObjectId
      ) || null;
      scheduleStats();
    };
    const handleCameraTelemetry = (event) => {
      pixelStatsTrackedObjectIdRef.current = normalizeTrackedObjectId(
        event.detail?.lookAtEvidence?.primaryObjectId
      ) || null;
      scheduleStats();
    };
    if (currentPortal === "meet-joz") {
      pixelStatsTrackedObjectIdRef.current = normalizeTrackedObjectId(
        resolvePortalSpatialView({
          portal: currentPortal,
          stage: currentStage,
          mesh: currentMesh,
        }).primaryObjectId
      ) || pixelStatsTrackedObjectIdRef.current;
    }
    scheduleStats();
    const refreshInterval = window.setInterval(scheduleStats, 250);
    window.addEventListener("world-scene-observed", handleSceneObserved);
    window.addEventListener("world-camera-telemetry", handleCameraTelemetry);
    return () => {
      window.removeEventListener("world-scene-observed", handleSceneObserved);
      window.removeEventListener("world-camera-telemetry", handleCameraTelemetry);
      window.clearInterval(refreshInterval);
      cancelScheduledStats();
    };
  }, [camera, currentMesh, currentPortal, currentStage, gl, scene]);

  useFrame((_, delta) => {
    const frameMs = Math.max(0, delta * 1000);
    smoothedFrameMsRef.current += (frameMs - smoothedFrameMsRef.current) * 0.18;
    rendererStatsElapsedRef.current += delta;
    if (rendererStatsElapsedRef.current < RENDERER_STATS_INTERVAL_SECONDS) return;
    rendererStatsElapsedRef.current = 0;

    publishScenePerformanceStats({
      rendererStats: {
        fps: Math.round(1000 / Math.max(1, smoothedFrameMsRef.current)),
        ms: Number(smoothedFrameMsRef.current.toFixed(1)),
      },
    });
  });

  useEffect(() => {
    if (!controls?.addEventListener) return undefined;
    const handleControlStart = () => {
      pointerControlActiveRef.current = true;
      cameraInteractionActiveRef.current = true;
      publishCanvasCameraInteraction(camera, true, cameraInteractionDirectionRef.current, {
        portal: currentPortal,
        stage: currentStage,
        mesh: currentMesh,
      });
    };
    const handleControl = () => {
      cameraInteractionActiveRef.current = true;
    };
    const handleCameraWake = () => {
      cameraInteractionActiveRef.current = true;
      publishCanvasCameraInteraction(camera, true, cameraInteractionDirectionRef.current, {
        portal: currentPortal,
        stage: currentStage,
        mesh: currentMesh,
      });
    };
    const handleControlEnd = () => {
      pointerControlActiveRef.current = false;
      cameraInteractionActiveRef.current = false;
      syncLiveAppContextRef.current = true;
      publishCanvasCameraInteraction(camera, false, cameraInteractionDirectionRef.current, {
        portal: currentPortal,
        stage: currentStage,
        mesh: currentMesh,
      });
    };
    const handleCameraRest = () => {
      if (pointerControlActiveRef.current) return;
      cameraInteractionActiveRef.current = false;
      syncLiveAppContextRef.current = true;
      publishCanvasCameraInteraction(camera, false, cameraInteractionDirectionRef.current, {
        portal: currentPortal,
        stage: currentStage,
        mesh: currentMesh,
      });
    };
    controls.addEventListener("controlstart", handleControlStart);
    controls.addEventListener("control", handleControl);
    controls.addEventListener("wake", handleCameraWake);
    controls.addEventListener("controlend", handleControlEnd);
    controls.addEventListener("rest", handleCameraRest);
    return () => {
      controls.removeEventListener("controlstart", handleControlStart);
      controls.removeEventListener("control", handleControl);
      controls.removeEventListener("wake", handleCameraWake);
      controls.removeEventListener("controlend", handleControlEnd);
      controls.removeEventListener("rest", handleCameraRest);
    };
  }, [camera, controls, currentMesh, currentPortal, currentStage]);

  const captureScene = useCallback(() => {
    if (typeof window === "undefined") return undefined;

    try {
      const activePortal = currentPortal || "root";
      const observedAt = new Date().toISOString();
      const observedScene =
        activePortal === "root"
          ? scene
          : window.__portalScenes?.[activePortal] || scene;
      const visibleObjectIds = [];
      const visibleMeshIds = [];
      const objectTransforms = [];
      const parentChildRelations = [];
      const visibleSemanticObjects = new Map();
      const cameraPosition = vectorValues(camera.position);
      const cameraDirectionVector = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      const cameraDirection = vectorValues(cameraDirectionVector);
      const previousCameraDirection = lastCameraDirectionRef.current.portal === activePortal
        ? lastCameraDirectionRef.current.direction
        : null;
      if (lastCameraDirectionRef.current.portal !== activePortal) {
        lastMeaningfulRotationRef.current = null;
      }
      const cameraView = describeCameraView(cameraDirection, previousCameraDirection);

      if (cameraView.rotation_direction !== "stable") {
        lastMeaningfulRotationRef.current = {
          from_sector: cameraView.previous_horizontal_sector,
          toward_sector: cameraView.horizontal_sector,
          direction: cameraView.rotation_direction,
          degrees: Math.abs(cameraView.rotation_delta_degrees),
          observed_at: observedAt,
        };
        lastCameraDirectionRef.current = { portal: activePortal, direction: cameraDirection };
      } else if (!previousCameraDirection) {
        lastCameraDirectionRef.current = { portal: activePortal, direction: cameraDirection };
      }
      const cameraViewState = {
        ...cameraView,
        yaw_velocity: 0,
        pitch_velocity: 0,
        stability: 100,
        last_rotation: lastMeaningfulRotationRef.current,
      };

      walkSceneGraph(observedScene, (object) => {
        if (!isEffectivelyVisible(object)) return;
        const semantic = semanticContext(object);
        const isSemanticRenderable = isVisibleRenderable(object);
        const existingSemantic = semantic?.id ? visibleSemanticObjects.get(semantic.id) : null;
        if (
          isSemanticRenderable
          && semantic?.id
          && (!existingSemantic || existingSemantic.spatial?.source === "parent-anchor")
        ) {
          const worldPosition = new THREE.Vector3();
          object.getWorldPosition(worldPosition);
          const spatial = describeObjectSpatialRelation({
            cameraPosition,
            cameraDirection,
            objectPosition: vectorValues(worldPosition),
          });
          visibleSemanticObjects.set(semantic.id, {
            ...semantic,
            spatial: spatial ? { ...spatial, source: "mesh-anchor" } : null,
          });
          semantic.children.forEach((child) => {
            const childId = String(child?.id || "").trim().toLowerCase();
            if (!childId || visibleSemanticObjects.has(childId)) return;
            visibleSemanticObjects.set(childId, {
              id: childId,
              portalId: child.portalId || semantic.portalId,
              role: child.role || "semantic child",
              meaning: child.meaning || null,
              children: [],
              interactive: typeof child.interactive === "boolean" ? child.interactive : false,
              action: child.action || null,
              target: child.target || null,
              sourceParentId: semantic.id,
              spatial: spatial ? { ...spatial, source: "parent-anchor" } : null,
            });
          });
        }
        if (visibleObjectIds.length >= MAX_SCENE_OBJECTS) return;
        const id = objectId(object);
        if (!id) return;
        visibleObjectIds.push(id);
        if (object.isMesh) visibleMeshIds.push(id);
        if (object.position && object.rotation && object.scale) {
          objectTransforms.push({
            id,
            position: vectorValues(object.position),
            rotation: [object.rotation.x, object.rotation.y, object.rotation.z],
            scale: vectorValues(object.scale),
            parentId: objectId(object.parent) || null,
          });
        }
        const parentId = objectId(object.parent);
        if (parentId) parentChildRelations.push({ subject: id, relation: "child-of", object: parentId });
      });

      const viewportWidth = gl?.domElement?.clientWidth || gl?.domElement?.width || null;
      const viewportHeight = gl?.domElement?.clientHeight || gl?.domElement?.height || null;
      const lookAtEvidence = resolvePortalSpatialView({
        portal: activePortal,
        stage: currentStage,
        mesh: currentMesh,
        yawDegrees: cameraView.yaw_degrees,
        pitchDegrees: cameraView.pitch_degrees,
      });
      const activeSemanticStage = getMaxxSemanticStage(currentStage);
      const availableSemanticActions = activePortal === "maxx"
        ? getMaxxSemanticActions({ isMobile })
        : [];
      const snapshot = {
        observedAt,
        worldxCenterEvidence: activePortal === "meet-joz" ? readWorldxCenterEvidence() : null,
        sceneState: {
          sceneId: activePortal,
          activePortal,
          activeStage: currentStage || null,
          focusedEntityId: currentMesh || null,
          visibleObjectIds,
          visibleMeshIds,
          objectTransforms,
          parentChildRelations,
          visibleSemanticObjects: [...visibleSemanticObjects.values()],
          semanticSequenceState: activeSemanticStage
            ? {
                id: activeSemanticStage.id,
                label: activeSemanticStage.label,
                meaning: activeSemanticStage.meaning,
              }
            : null,
          availableSemanticActions,
          animationState: currentStage || currentMesh || null,
        },
        cameraState: {
          position: cameraPosition,
          direction: cameraDirection,
          view: cameraViewState,
          projection: {
            type: camera.isPerspectiveCamera ? "perspective" : camera.isOrthographicCamera ? "orthographic" : "unknown",
            fov: camera.fov,
            aspect: camera.aspect,
            near: camera.near,
            far: camera.far,
          },
          viewport: {
            width: viewportWidth,
            height: viewportHeight,
            pixelRatio: typeof gl?.getPixelRatio === "function" ? gl.getPixelRatio() : null,
          },
        },
        lookAtEvidence,
        runtimeStatus: {
          renderer: "react-three-fiber",
          viewportWidth,
          viewportHeight,
          loading: false,
          animating: Boolean(currentStage),
        },
        spatialRelationships: parentChildRelations,
        sourceVersions: { renderer: "react-three-fiber-structured-v2" },
      };
      snapshot.sceneState.semanticActionVerification = verifyPendingMaxxAction(snapshot);
      window.__sceneObservationSnapshot = snapshot;
      publishLiveAppContext({ sceneSnapshot: snapshot });

      const signature = JSON.stringify({
        portal: snapshot.sceneState.activePortal,
        stage: snapshot.sceneState.activeStage,
        focus: snapshot.sceneState.focusedEntityId,
        semanticObjects: snapshot.sceneState.visibleSemanticObjects.map((object) => object.id),
        cameraSector: snapshot.cameraState.view.horizontal_sector,
        cameraYaw10: Math.round(snapshot.cameraState.view.yaw_degrees / 10),
        cameraPitch10: Math.round(snapshot.cameraState.view.pitch_degrees / 10),
        lastRotation: snapshot.cameraState.view.last_rotation,
        actions: snapshot.sceneState.availableSemanticActions.map((action) => action.id || action.action),
        verification: snapshot.sceneState.semanticActionVerification?.status || null,
      });
      if (signature !== lastPublishedSignatureRef.current) {
        lastPublishedSignatureRef.current = signature;
        window.dispatchEvent(new CustomEvent("world-scene-observed", { detail: snapshot }));
      }
    } catch (error) {
      window.__sceneObservationFailure = {
        message: String(error?.message || error),
        recordedAt: new Date().toISOString(),
      };
    }

    return undefined;
  }, [camera, currentMesh, currentPortal, currentStage, gl, isMobile, scene]);

  const scheduleFullCapture = useCallback(() => {
    if (fullCaptureTaskRef.current) return;

    const run = () => {
      fullCaptureTaskRef.current = null;
      captureScene();
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      fullCaptureTaskRef.current = {
        type: "idle",
        id: window.requestIdleCallback(run, { timeout: 400 }),
      };
      return;
    }

    fullCaptureTaskRef.current = {
      type: "timeout",
      id: window.setTimeout(run, 0),
    };
  }, [captureScene]);

  useEffect(() => {
    scheduleFullCapture();
    return () => {
      const task = fullCaptureTaskRef.current;
      if (!task) return;
      if (task.type === "idle" && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(task.id);
      } else {
        window.clearTimeout(task.id);
      }
      fullCaptureTaskRef.current = null;
    };
  }, [scheduleFullCapture]);

  useFrame(({ clock }) => {
    const activePortal = currentPortal || "root";
    const elapsed = clock.getElapsedTime();
    // CameraControls owns every frame while the camera is moving. Spatial
    // observation resumes exactly once after both pointer input and damping
    // have settled, keeping all HUD/AI work out of the interaction path.
    if (cameraInteractionActiveRef.current) {
      if (elapsed - lastCameraInteractionPublishAtRef.current >= 1 / 30) {
        lastCameraInteractionPublishAtRef.current = elapsed;
        const previousDirection = lastCameraDirectionRef.current.portal === activePortal
          ? lastCameraDirectionRef.current.direction
          : null;
        publishCanvasCameraInteraction(
          camera,
          true,
          cameraInteractionDirectionRef.current,
          { portal: currentPortal, stage: currentStage, mesh: currentMesh },
          previousDirection,
        );
        lastCameraDirectionRef.current = {
          portal: activePortal,
          direction: vectorValues(cameraInteractionDirectionRef.current),
        };
      }
      return;
    }
    if (typeof window === "undefined") return;
    const currentSnapshot = window.__sceneObservationSnapshot;
    if (currentSnapshot?.sceneState?.activePortal !== activePortal) {
      // Route transitions can publish the new app portal before the renderer
      // has emitted its first snapshot for that portal. Recover synchronously
      // on the next render frame so Joz never receives a root portal with a
      // stale or missing camera-grounded target.
      if (portalRecoveryRef.current !== activePortal) {
        portalRecoveryRef.current = activePortal;
        lastCameraDirectionRef.current = { portal: null, direction: null };
        lastMeaningfulRotationRef.current = null;
        captureScene();
        if (window.__sceneObservationSnapshot?.sceneState?.activePortal !== activePortal) {
          portalRecoveryRef.current = null;
        }
      }
      return;
    }
    portalRecoveryRef.current = null;
    if (!syncLiveAppContextRef.current) return;
    if (!currentSnapshot) return;
    const snapshot = currentSnapshot;
    const cameraDirectionVector = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const cameraDirection = vectorValues(cameraDirectionVector);
    const previousCameraDirection = lastCameraDirectionRef.current.portal === (currentPortal || "root")
      ? lastCameraDirectionRef.current.direction
      : null;
    const cameraView = describeCameraView(cameraDirection, previousCameraDirection);
    const dynamics = cameraDynamics(cameraView, null, elapsed, activePortal);
    if (cameraView.rotation_direction !== "stable") {
      lastMeaningfulRotationRef.current = {
        from_sector: cameraView.previous_horizontal_sector,
        toward_sector: cameraView.horizontal_sector,
        direction: cameraView.rotation_direction,
        degrees: Math.abs(cameraView.rotation_delta_degrees),
        observed_at: new Date().toISOString(),
      };
    }
    lastCameraDirectionRef.current = { portal: activePortal, direction: cameraDirection };
    const lookAtEvidence = resolvePortalSpatialView({
      portal: currentPortal || "root",
      stage: currentStage,
      mesh: currentMesh,
      yawDegrees: cameraView.yaw_degrees,
      pitchDegrees: cameraView.pitch_degrees,
    });
    const nextSnapshot = {
      cameraOnly: true,
      ...snapshot,
      observedAt: new Date().toISOString(),
      worldxCenterEvidence: activePortal === "meet-joz" ? readWorldxCenterEvidence() : null,
      cameraState: {
        ...snapshot.cameraState,
        position: vectorValues(camera.position),
        direction: cameraDirection,
        view: {
          ...snapshot.cameraState?.view,
          ...cameraView,
          ...dynamics,
          last_rotation: lastMeaningfulRotationRef.current,
        },
      },
      sceneState: {
        ...snapshot.sceneState,
      },
      lookAtEvidence,
      runtimeStatus: {
        ...snapshot.runtimeStatus,
        interacting: cameraInteractionActiveRef.current,
      },
    };
    window.__sceneObservationSnapshot = nextSnapshot;
    publishLiveAppContext({ sceneSnapshot: nextSnapshot });
    syncLiveAppContextRef.current = false;
    window.dispatchEvent(new CustomEvent("world-scene-observed", { detail: nextSnapshot }));
  });

  return null;
}
